import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PageLayout } from '../layouts/PageLayout';
import { SEOHead } from '../components/seo/SEOHead';
import { GlassPanel } from '../components/ui/GlassPanel';
import { Badge } from '../components/ui/Badge';
import { parseFrontmatter } from '../lib/frontmatter';
import { SITE } from '../utils/seo';

const PROJECT_FILES = ['example-project'];

function useProjectList() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all(
      PROJECT_FILES.map((slug) =>
        fetch(`/content/projects/${slug}.md`)
          .then((r) => r.text())
          .then((text) => {
            const { frontmatter } = parseFrontmatter(text);
            return { slug, ...frontmatter };
          })
          .catch(() => null)
      )
    ).then((results) => {
      setProjects(results.filter(Boolean));
      setLoading(false);
    });
  }, []);

  return { projects, loading };
}

export function Projects() {
  const { projects, loading } = useProjectList();

  return (
    <PageLayout>
      <SEOHead
        title="Projects"
        description="Projects and work by Simone Carani."
        canonical={`${SITE.url}/projects`}
      />

      <header className="mb-10">
        <h1 className="font-serif text-4xl text-[var(--color-text)] mb-2">Projects</h1>
        <p className="text-[var(--color-muted)]">Selected work and open source contributions.</p>
      </header>

      {loading && <p className="text-[var(--color-muted)] animate-pulse">Loading…</p>}

      <ul className="flex flex-col gap-4">
        {projects.map((project) => (
          <li key={project.slug}>
            <GlassPanel
              as={Link}
              to={`/projects/${project.slug}`}
              className="group block p-5 hover:bg-white/[0.06] hover:border-[var(--color-accent)]/20 transition-all no-underline"
              aria-label={`View project: ${project.title}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-semibold text-[var(--color-text)] group-hover:text-[var(--color-accent)] transition-colors truncate">
                    {project.title}
                  </h2>
                  {project.description && (
                    <p className="text-sm text-[var(--color-muted)] mt-1 line-clamp-2">
                      {project.description}
                    </p>
                  )}
                  {Array.isArray(project.tags) && project.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {project.tags.map((tag) => (
                        <Badge key={tag}>{tag}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <span aria-hidden="true" className="text-[var(--color-muted)]/40 group-hover:text-[var(--color-accent)] transition-colors mt-0.5 flex-shrink-0">
                  →
                </span>
              </div>
            </GlassPanel>
          </li>
        ))}
      </ul>
    </PageLayout>
  );
}
